import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { tapHaptic } from '../../shared/lib/haptics';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PageHeader } from '../../shared/ui/PageHeader';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { useToast } from '../../shared/ui/toast-context';

import { ADMIN_QUERY_KEYS, addSuperadmin, listSuperadmins, provisionUser } from './api';
import { addSuperadminSchema, type AddSuperadminInput } from './schemas';
import s from './SuperadminsPage.module.css';

function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '?';
  const parts = local.split(/[._-]/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

const SKELETON_ROWS = [0, 1, 2] as const;

/** Mirrors `.row` — avatar, email, "added …" date. */
function SuperadminRowSkeleton(): JSX.Element {
  return (
    <li className={s.row}>
      <Skeleton variant="circle" width={32} height={32} />
      <Skeleton width="50%" height={14} className={s.rowName ?? ''} />
      <Skeleton width={96} height={12} className={s.rowMeta ?? ''} />
    </li>
  );
}

export function AdminSuperadminsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ADMIN_QUERY_KEYS.superadmins,
    queryFn: listSuperadmins,
  });

  const mutate = useMutation({
    mutationFn: async (values: AddSuperadminInput) => {
      let provisioned = false;
      if (values.password !== '') {
        await provisionUser({ email: values.email, password: values.password });
        provisioned = true;
      }
      const row = await addSuperadmin(values.email);
      return { row, provisioned };
    },
    onSuccess: ({ row, provisioned }) => {
      toast.show(
        provisioned
          ? `Promoted ${row.email} to superadmin — they can log in with the password you set.`
          : `Promoted ${row.email} to superadmin.`,
        { tone: 'success' },
      );
      tapHaptic();
      reset();
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.superadmins });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddSuperadminInput>({
    resolver: zodResolver(addSuperadminSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: AddSuperadminInput): Promise<void> {
    setServerError(null);
    try {
      await mutate.mutateAsync(values);
    } catch {
      /* onError set the serverError message */
    }
  }

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Privileged access"
        title="Superadmins"
        subtitle="Promote another signed-up user to superadmin. Be careful — superadmins can manage every institute and promote other superadmins."
        meta={
          <span className={s.warning} aria-hidden="true">
            Highest privilege
          </span>
        }
      />

      <section className={s.section}>
        <div className={s.sectionHead}>
          <span className={s.sectionIcon} aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 1.5 9.85 5.3l4.15.6-3 2.95.7 4.15L8 11.05 4.3 13l.7-4.15L2 5.9l4.15-.6L8 1.5z" />
            </svg>
          </span>
          <h2 className={s.sectionTitle}>Promote user</h2>
        </div>
        <form
          noValidate
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className={s.form}
        >
          <div>
            <Label htmlFor="super_email">Email</Label>
            <Input
              id="super_email"
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="trusted@example.com"
              invalid={errors.email !== undefined}
              aria-describedby={errors.email ? 'super_email-error' : undefined}
              {...register('email')}
            />
            <FormError id="super_email-error" message={errors.email?.message} />
          </div>
          <Button type="submit" className={s.formSubmit} loading={isSubmitting || mutate.isPending}>
            Promote
          </Button>
          <div className={s.formFull}>
            <Label htmlFor="super_password">Password (optional)</Label>
            <Input
              id="super_password"
              type="password"
              autoComplete="new-password"
              placeholder="Min 8 chars. Leave blank if user has already signed up."
              invalid={errors.password !== undefined}
              aria-describedby={errors.password ? 'super_password-error' : undefined}
              {...register('password')}
            />
            <FormError id="super_password-error" message={errors.password?.message} />
          </div>

          {serverError !== null ? (
            <div className={s.formFull}>
              <StatusBanner tone="error">{serverError}</StatusBanner>
            </div>
          ) : null}
        </form>
      </section>

      <section>
        <div className={s.subHead}>
          <h2 className={s.subTitle}>Current superadmins</h2>
          <span className={s.subMeta}>{(list.data ?? []).length} total</span>
        </div>
        {list.isPending ? (
          <ul className={s.list} role="status" aria-label="Loading superadmins">
            {SKELETON_ROWS.map((row) => (
              <SuperadminRowSkeleton key={row} />
            ))}
          </ul>
        ) : list.error !== null ? (
          <StatusBanner tone="error">Could not load superadmins: {list.error.message}</StatusBanner>
        ) : (list.data ?? []).length === 0 ? (
          <p className={s.empty}>No superadmins yet — this is unusual.</p>
        ) : (
          <ul className={s.list}>
            {list.data?.map((sa) => (
              <li key={sa.user_id} className={s.row}>
                <span className={s.avatar} aria-hidden="true">
                  {initialsFromEmail(sa.email)}
                </span>
                <span className={s.rowName}>{sa.email}</span>
                <span className={s.rowMeta}>
                  added {new Date(sa.added_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
